<script setup>
import { computed, ref, watch } from 'vue'
import { fechaTexto } from './fecha'
import MayoresVisorMapa from '@/components/SalidaPublicaMayores/MayoresVisorMapa.vue'
import MayoresMapaCompleto from '@/components/SalidaPublicaMayores/MayoresMapaCompleto.vue'

const props = defineProps({
  salida: { type: Object, required: true },
  mapaUrl: { type: Function, required: true },
  enviando: Boolean,
  error: { type: String, default: null },
})

const emit = defineEmits(['enviar'])

// Territorios con manzanas numeradas se informan manzana por manzana (dos
// columnas: sin trabajar / trabajadas). Los que no tienen, con sí/no.
const conManzanas = (t) => (t.manzanas || []).length > 0

// Borrador guardado en este teléfono: si el conductor sale de la pantalla antes
// de enviar, al volver encuentra lo que ya había marcado.
const draftKey = computed(() => `informe-borrador-v2-${props.salida.id}`)
const leerBorrador = () => {
  try {
    return JSON.parse(localStorage.getItem(draftKey.value) || 'null') || {}
  } catch {
    return {}
  }
}
const borrador = leerBorrador()

// key → manzanas trabajadas. Arranca con lo ya marcado ese día (por ejemplo en
// vivo desde el enlace de la salida).
const trabajadas = ref(
  borrador.trabajadas ||
    Object.fromEntries(props.salida.territorios.filter(conManzanas).map((t) => [t.key, [...(t.trabajadas || [])]]))
)
// key → true / false para territorios sin manzanas; sin clave = sin responder
const respuestas = ref(borrador.respuestas || {})
const notas = ref(borrador.notas || '')

watch([trabajadas, respuestas, notas], () => {
  try {
    localStorage.setItem(draftKey.value, JSON.stringify({
      trabajadas: trabajadas.value,
      respuestas: respuestas.value,
      notas: notas.value,
    }))
  } catch { /* sin almacenamiento: el borrador no se guarda */ }
}, { deep: true })

const trabajadasDe = (t) => trabajadas.value[t.key] || []

// Vista de cada territorio: 'completo' (mapa con todas las manzanas, tocar una
// la marca) o 'manzana' (lista con imagen y casilla). Por defecto, el mapa.
const vistas = ref({})
const vistaDe = (t) => vistas.value[t.key] || 'completo'
const cambiarVista = (t, vista) => {
  vistas.value = { ...vistas.value, [t.key]: vista }
}

const datosMapa = (t) => ({
  limits: t.mapa?.limits || [],
  color: t.mapa?.color || {},
  manzanas: (t.mapa?.manzanas || []).map(({ idx, latlng }) => ({
    idx,
    latlng,
    trabajada: trabajadasDe(t).includes(idx),
  })),
})

const moverManzana = (t, m) => {
  const actuales = trabajadasDe(t)
  trabajadas.value = {
    ...trabajadas.value,
    [t.key]: actuales.includes(m)
      ? actuales.filter((x) => x !== m)
      : [...actuales, m].sort((a, b) => a - b),
  }
}

// Imágenes de manzana: si el servicio de mapas no responde se muestra el número.
const mapaConError = ref(new Set())
const errorKey = (t, m) => `${t.key}|${m}`
const onErrorMapa = (t, m) => {
  mapaConError.value = new Set([...mapaConError.value, errorKey(t, m)])
}
const tieneMapa = (t, m) => !mapaConError.value.has(errorKey(t, m))

// Visor en pantalla completa
const abierta = ref(null)
const verManzana = (t, m) => { abierta.value = { key: t.key, m } }

const responder = (key, valor) => {
  respuestas.value = { ...respuestas.value, [key]: valor }
}

const sinResponder = computed(() =>
  props.salida.territorios.filter((t) => !conManzanas(t) && typeof respuestas.value[t.key] !== 'boolean')
)

const resumen = computed(() =>
  props.salida.territorios.map((t) => {
    if (conManzanas(t)) {
      const n = trabajadasDe(t).length
      return {
        key: t.key,
        trabajado: n > 0,
        texto: n === 0
          ? 'no se trabajó'
          : n === t.manzanas.length
            ? 'se trabajaron todas las manzanas'
            : `${n} de ${t.manzanas.length} manzanas trabajadas`,
      }
    }
    const r = respuestas.value[t.key]
    return {
      key: t.key,
      trabajado: r === true,
      texto: r === true ? 'se trabajó' : r === false ? 'no se trabajó' : 'falta responder',
    }
  })
)

const intentoEnviar = ref(false)

const enviar = () => {
  intentoEnviar.value = true
  if (sinResponder.value.length) return
  emit('enviar', {
    territorios: props.salida.territorios.map((t) =>
      conManzanas(t)
        ? { key: t.key, trabajado: trabajadasDe(t).length > 0, manzanas: trabajadasDe(t) }
        : { key: t.key, trabajado: respuestas.value[t.key], manzanas: [] }
    ),
    notas: notas.value.trim(),
  })
}

// "No se predicó": la salida queda informada con todos sus territorios como no
// trabajados, sin contar manzanas ni respuestas. Pide confirmación porque
// después no se puede volver a informar.
const confirmandoNoPredicada = ref(false)

const noPredicada = () => {
  emit('enviar', {
    territorios: props.salida.territorios.map((t) => ({ key: t.key, trabajado: false, manzanas: [] })),
    notas: notas.value.trim(),
  })
}

defineExpose({
  limpiarBorrador: () => {
    try { localStorage.removeItem(draftKey.value) } catch { /* noop */ }
  },
})
</script>

<template>
  <div class="mayores-contenido">
    <p class="tarjeta-lista__titulo" style="margin-bottom:var(--space-1)">
      <i class="pi pi-calendar" aria-hidden="true" /> {{ fechaTexto(salida.date) }}
    </p>
    <p v-if="salida.hour" class="tarjeta-lista__detalle">
      <i class="pi pi-clock" aria-hidden="true" /> {{ salida.hour }} hs
    </p>
    <p v-if="salida.puntoEncuentro" class="tarjeta-lista__detalle">
      <i class="pi pi-map-marker" aria-hidden="true" /> {{ salida.puntoEncuentro }}
    </p>

    <p class="mayores-saludo" style="font-size:calc(var(--step-2) * var(--text-scale))">
      ¿Qué se trabajó?
    </p>
    <p class="campo__ayuda" style="margin-top:0">
      Marcá la casilla <strong>Se trabajó</strong> en cada manzana que se trabajó,
      o tocá el número de la manzana en el mapa completo.
      Para ver un mapa más grande, tocá <strong>Ver grande</strong>.
    </p>

    <section
      v-for="t in salida.territorios"
      :key="t.key"
      class="mayores-pregunta"
      :class="{ 'mayores-pregunta--respondida': conManzanas(t) ? trabajadasDe(t).length : typeof respuestas[t.key] === 'boolean' }"
      :aria-label="`Territorio ${t.key}`"
    >
      <p class="mayores-terr-fila__numero">Territorio {{ t.key }}</p>
      <p v-if="t.referencia" class="campo__ayuda" style="margin:0 0 var(--space-2)">{{ t.referencia }}</p>

      <!-- Con manzanas: mapa completo o lista en orden fijo, cada una con su casilla -->
      <template v-if="conManzanas(t)">
        <template v-if="t.mapa">
          <div class="mayores-vista-toggle" role="group" :aria-label="`Forma de ver el territorio ${t.key}`">
            <button
              type="button"
              class="mayores-vista-toggle__boton"
              :class="{ 'mayores-vista-toggle__boton--activo': vistaDe(t) === 'completo' }"
              :aria-pressed="vistaDe(t) === 'completo'"
              @click="cambiarVista(t, 'completo')"
            >
              <i class="pi pi-map" aria-hidden="true" /> Mapa completo
            </button>
            <button
              type="button"
              class="mayores-vista-toggle__boton"
              :class="{ 'mayores-vista-toggle__boton--activo': vistaDe(t) === 'manzana' }"
              :aria-pressed="vistaDe(t) === 'manzana'"
              @click="cambiarVista(t, 'manzana')"
            >
              <i class="pi pi-th-large" aria-hidden="true" /> Por manzana
            </button>
          </div>

          <MayoresMapaCompleto
            v-if="vistaDe(t) === 'completo'"
            :limits="datosMapa(t).limits"
            :color="datosMapa(t).color"
            :manzanas="datosMapa(t).manzanas"
            :etiqueta="t.key"
            @clic-manzana="(m) => moverManzana(t, m)"
          />
        </template>

        <ul
          v-if="!t.mapa || vistaDe(t) === 'manzana'"
          class="mayores-mz-lista"
          :aria-label="`Manzanas del territorio ${t.key}`"
        >
          <li
            v-for="m in t.manzanas"
            :key="m"
            class="mayores-mz-fila"
            :class="{ 'mayores-mz-fila--trabajada': trabajadasDe(t).includes(m) }"
          >
            <button
              type="button"
              class="mayores-mz-fila__imagen"
              :disabled="!tieneMapa(t, m)"
              :aria-label="`Ver grande el mapa de la manzana ${m}`"
              @click="verManzana(t, m)"
            >
              <img
                v-if="tieneMapa(t, m)"
                :src="mapaUrl(t.key, m)"
                :alt="`Mapa de la manzana ${m} del territorio ${t.key}`"
                loading="lazy"
                class="mayores-mapa-celda__img"
                @error="onErrorMapa(t, m)"
              >
              <span v-else class="mayores-mz-fila__sin-mapa">{{ m }}</span>
              <span v-if="tieneMapa(t, m)" class="mayores-mz-fila__lupa" aria-hidden="true">
                <i class="pi pi-search-plus" /> Ver grande
              </span>
            </button>

            <div class="mayores-mz-fila__cuerpo">
              <p class="mayores-mz-fila__titulo">Manzana {{ m }}</p>
              <button
                type="button"
                role="checkbox"
                :aria-checked="trabajadasDe(t).includes(m)"
                :aria-label="`Manzana ${m}: se trabajó`"
                class="mayores-mz-check"
                @click="moverManzana(t, m)"
              >
                <span class="mayores-mz-check__caja" aria-hidden="true">
                  <i v-if="trabajadasDe(t).includes(m)" class="pi pi-check" />
                </span>
                Se trabajó
              </button>
            </div>
          </li>
        </ul>
      </template>

      <!-- Sin manzanas numeradas: sí / no -->
      <div
        v-else
        class="mayores-opciones"
        role="radiogroup"
        :aria-label="`¿Se trabajó el territorio ${t.key}?`"
      >
        <button
          type="button"
          role="radio"
          :aria-checked="respuestas[t.key] === true"
          class="mayores-opcion mayores-opcion--si"
          :class="{ 'mayores-opcion--activa': respuestas[t.key] === true }"
          @click="responder(t.key, true)"
        >
          <i class="pi pi-check" aria-hidden="true" />
          Sí, se trabajó
        </button>
        <button
          type="button"
          role="radio"
          :aria-checked="respuestas[t.key] === false"
          class="mayores-opcion mayores-opcion--no"
          :class="{ 'mayores-opcion--activa': respuestas[t.key] === false }"
          @click="responder(t.key, false)"
        >
          <i class="pi pi-times" aria-hidden="true" />
          No se trabajó
        </button>
      </div>
    </section>

    <label class="campo__etiqueta" for="informe-notas" style="margin-top:var(--space-3)">Notas (si querés)</label>
    <p class="campo__ayuda">Por ejemplo: por qué quedaron manzanas sin trabajar.</p>
    <textarea
      id="informe-notas"
      v-model="notas"
      class="mayores-textarea"
      maxlength="2000"
      rows="4"
    />

    <div class="tarjeta-accion mayores-resumen" aria-live="polite">
      <p class="campo__etiqueta" style="margin:0 0 var(--space-1)">Vas a informar:</p>
      <p v-for="r in resumen" :key="r.key" class="mayores-resumen__fila">
        <i :class="r.trabajado ? 'pi pi-check' : 'pi pi-times'" aria-hidden="true" />
        <span><strong>Territorio {{ r.key }}</strong>: {{ r.texto }}</span>
      </p>
    </div>

    <p v-if="intentoEnviar && sinResponder.length" class="mayores-aviso" role="alert">
      <i class="pi pi-info-circle" aria-hidden="true" />
      <span>
        Falta responder si se trabajó
        {{ sinResponder.length === 1 ? 'el territorio' : 'los territorios' }}
        {{ sinResponder.map((t) => t.key).join(', ') }}.
      </span>
    </p>
    <p v-if="error" class="mayores-aviso" role="alert">
      <i class="pi pi-exclamation-circle" aria-hidden="true" />
      <span>{{ error }}</span>
    </p>

    <MayoresVisorMapa
      :src="abierta ? mapaUrl(abierta.key, abierta.m) : null"
      :alt="abierta ? `Mapa de la manzana ${abierta.m} del territorio ${abierta.key}` : ''"
      :etiqueta="abierta ? `Mapa de la manzana ${abierta.m}` : ''"
      @cerrar="abierta = null"
    />

    <button
      type="button"
      class="boton-primario mayores-separacion"
      :disabled="enviando"
      @click="enviar"
    >
      <i :class="enviando ? 'pi pi-spin pi-spinner' : 'pi pi-send'" aria-hidden="true" />
      {{ enviando ? 'Enviando...' : 'Enviar informe' }}
    </button>

    <div v-if="!confirmandoNoPredicada" class="mayores-separacion">
      <button
        type="button"
        class="boton-secundario"
        :disabled="enviando"
        @click="confirmandoNoPredicada = true"
      >
        <i class="pi pi-ban" aria-hidden="true" /> No se predicó esta salida
      </button>
    </div>

    <div v-else class="tarjeta-accion mayores-separacion" role="group" aria-label="Confirmar que no se predicó">
      <p class="campo__etiqueta" style="margin:0 0 var(--space-2)">
        ¿Confirmás que no se predicó esta salida? Quedará informada sin territorios trabajados.
      </p>
      <button type="button" class="boton-primario" :disabled="enviando" @click="noPredicada">
        <i :class="enviando ? 'pi pi-spin pi-spinner' : 'pi pi-check'" aria-hidden="true" />
        {{ enviando ? 'Enviando...' : 'Sí, no se predicó' }}
      </button>
      <button
        type="button"
        class="boton-secundario mayores-separacion"
        :disabled="enviando"
        @click="confirmandoNoPredicada = false"
      >
        Cancelar
      </button>
    </div>
  </div>
</template>
